import React, { useEffect, useState } from "react";
import { api, asList, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, Camera as CameraIcon } from "lucide-react";

/** Watchman check-in dialog (optionally pre-filled from an appointment). */
export default function CheckInDialog({ open, appt, onClose, onDone }) {
  const [f, setF] = useState({
    name: "", phone: "", company: "", host_user_id: "", purpose: "", vehicle_number: "", photo_url: "",
  });
  const [hosts, setHosts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (appt) {
      setF({
        name: appt.visitor_name || "",
        phone: appt.visitor_phone || "",
        company: appt.visitor_company || "",
        host_user_id: appt.host_user_id || "",
        purpose: appt.purpose || "",
        vehicle_number: "",
        photo_url: "",
      });
    } else {
      setF({ name: "", phone: "", company: "", host_user_id: "", purpose: "", vehicle_number: "", photo_url: "" });
    }
    (async () => {
      try { const { data } = await api.get("/users"); setHosts(asList(data)); }
      catch (err) { void err; }
    })();
  }, [open, appt]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((x) => ({ ...x, photo_url: data.url }));
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.name.trim()) { setError("Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, appointment_id: appt?.id || null };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post("/visitors/check-in", payload);
      toast.success("Visitor checked in");
      onDone();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="visitor-checkin-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Visitor Check-in</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 flex items-center gap-3">
            {f.photo_url ? (
              <img src={fileUrl(f.photo_url)} alt="" className="w-16 h-16 rounded-full object-cover border border-[#E4DFCF]"/>
            ) : (
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#E4DFCF] flex items-center justify-center text-[#4A5A50]">
                <CameraIcon size={20}/>
              </div>
            )}
            <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-[#0F5E39] underline">
              {uploading ? "Uploading…" : (f.photo_url ? "Replace photo" : "Take photo")}
              <input type="file" accept="image/*" capture="user" className="hidden" onChange={onPhoto} data-testid="visitor-photo-input"/>
            </label>
          </div>
          <div className="col-span-2"><Label>Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="visitor-name"/>
          </div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}/></div>
          <div><Label>Company</Label><Input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })}/></div>
          <div className="col-span-2"><Label>Host</Label>
            <Select value={f.host_user_id || undefined} onValueChange={(v) => setF({ ...f, host_user_id: v })}>
              <SelectTrigger data-testid="visitor-host"><SelectValue placeholder="Pick host"/></SelectTrigger>
              <SelectContent className="max-h-64">
                {hosts.map((h) => <SelectItem key={h.id} value={h.id}>{h.name} · {h.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Purpose</Label><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })}/></div>
          <div className="col-span-2"><Label>Vehicle no.</Label><Input value={f.vehicle_number} onChange={(e) => setF({ ...f, vehicle_number: e.target.value })}/></div>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[#0F5E39] hover:bg-[#083F22] text-white"
            data-testid="visitor-submit-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Check size={14} className="mr-1"/>}
            Check in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
